<template>
    <div class="catalog-product-applications">
        <div class="catalog-product-applications__list pb-4">
            <div v-if="selectedShops.length === 0">
                <em>No applications assigned</em>
            </div>
            <div
                v-for="(application, key) in selectedShops"
                class="d-flex align-items-center"
                :key="key"
            >
                <div class="flex-1 mr-2">
                    <span>{{application.applicationName}}</span>
                    <status-chip class="ml-2">{{application.status}}</status-chip>
                </div>

                <div>
                    <mercur-menu>
                        <mercur-button class="btn btn-icon"><i class="fas fa-ellipsis-v"></i></mercur-button>
                        <div slot="dropdown">
                            <mercur-item @click.native="deleteApplication(application)" :disabled="selectedShops.length <= 1"><i class="fas fa-trash"></i><span>Delete</span></mercur-item>
                        </div>
                    </mercur-menu>
                </div>
            </div>
        </div>
        <mercur-button class="btn btn-raised w-100 mt-2" @click="isApplicationSelectDialogOpen = true">Modify applications</mercur-button>
        <mercur-dialog
            :is-open.sync="isApplicationSelectDialogOpen"
            :width="'900px'"
            @close="closeApplicationSelectionDialog"
        >
            <form @submit.prevent="saveSelectedApplications(applicationDialogPendingApplications)">
                <div class="catalog-product-applications__dialog">
                    <application-selector
                        v-if="isApplicationSelectDialogOpen"
                        class="h-100 d-flex flex-column"
                        :active-step.sync="applicationSelectionWizard"
                        :value="value"
                        @input="applicationDialogPendingApplications = $event"
                        :product="product"
                    ></application-selector>
                </div>
                <mercur-spinner v-if="isPending"></mercur-spinner>
                <div slot="footer" v-if="hasApplicationChanges">
                    <mercur-button class="btn" :disabled="isPending" @click.native="closeApplicationSelectionDialog">Close</mercur-button>
                    <mercur-button class="btn btn-primary btn-raised" type="submit" :disabled="isPending || !hasApplicationChanges">Update product</mercur-button>
                </div>
            </form>
        </mercur-dialog>
    </div>
</template>
<script>
import { mapState } from 'vuex'
import CONFIG from '@root/config'
import StatusChip from '../../utils/StatusChip'
import ApplicationSelector from '../../../components/elements/catalog/ApplicationSelector'

export default {
    name: 'CatalogProductAssignedApplications',
    components: { ApplicationSelector, StatusChip },
    props: {
        value: { },
        catalogProductId: {
            type: String,
            required: true,
        },
        product: { },
    },
    data () {
        return {
            applicationSelectionWizard: 0,
            isApplicationSelectDialogOpen: false,
            applicationDialogPendingApplications: [],
            isPending: false,
            hasApplicationChanges: false,
        }
    },
    computed: {
        ...mapState('shop', ['shops']),
        selectedShops () {
            let val = this.value
            if (this.value && !Array.isArray(this.value)) {
                val = Object.values(this.value)
            }
            return val.map(selectedApplication => {
                const application = this.shops.find(availableShop => availableShop.applicationId === selectedApplication.applicationId)

                return {
                    applicationName: application ? application.applicationName : application.applicationId,
                    ...selectedApplication,
                }
            })
        },
    },
    methods: {
        deleteApplication (applicationToRemove) {
            const applicationSet = this.selectedShops.filter(selectedShop => selectedShop.applicationId !== applicationToRemove.applicationId)
            this.saveSelectedApplications(applicationSet)
        },
        saveSelectedApplications (applications) {
            this.isPending = true
            const payload = {
                catalogProductId: this.catalogProductId,
                applications,
            }

            this.post(
                CONFIG.API.ROUTES.CATALOG.SET_CATALOG_PRODUCT_APPLICATIONS,
                payload,
                'Applications were successfully set',
                'Something went wrong. Please try again.'
            ).then(({ data }) => {
                this.$set(this, 'isApplicationSelectDialogOpen', false)
                setTimeout(() => {
                    this.$emit('forceFetchCatalogProduct')
                }, 100)
            }).finally(() => {
                this.isPending = false
            })
        },
        closeApplicationSelectionDialog () {
            this.applicationDialogPendingApplications = []
            this.isApplicationSelectDialogOpen = false
        },
        getAssignedApplicationsByApplicationId (applicationId) {
            return this.product.assignedApplications.filter(assignedApplication => assignedApplication.applicationId === applicationId)
        },
        getAssignedApplicationsByAgreementId (assignedApplications, agreementId) {
            return assignedApplications.find(assignedApplication => assignedApplication.agreementId === agreementId)
        },
        hasChangesInAssignedCountries (assignedCountries, pendingCountries) {
            if (assignedCountries.length !== pendingCountries.length) {
                return true
            }

            const assignedCountriesSorted = [...assignedCountries].sort()
            const pendingCountriesSorted = [...pendingCountries].sort()

            for (let i = 0; i < assignedCountriesSorted.length; i++) {
                if (assignedCountriesSorted[i] !== pendingCountriesSorted[i]) {
                    return true
                }
            }

            return false
        },
    },
    watch: {
        applicationDialogPendingApplications () {
            if (!this.applicationDialogPendingApplications.length) {
                this.hasApplicationChanges = false
                return
            }

            this.hasApplicationChanges = this.applicationDialogPendingApplications.some(pendingApplication => {
                const assignedApplications = this.getAssignedApplicationsByApplicationId(pendingApplication.applicationId)
                if (!assignedApplications.length) {
                    return true
                }

                return pendingApplication.agreements.some(pendingAgreement => {
                    const assignedApplication = this.getAssignedApplicationsByAgreementId(assignedApplications, pendingAgreement.agreementId)
                    if (!assignedApplication) {
                        return true
                    }

                    return this.hasChangesInAssignedCountries(assignedApplication.assignedCountries, pendingAgreement.countries)
                })
            })
        },
    },
    created () {
        if (this.shops.length) {
            return
        }

        this.fetchShops().then(() => {
            this.checkConfig()
        })
    },
}

</script>

<style lang="scss" scoped>
.catalog-product-applications {
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &__list {
        overflow-y: auto;
        overflow-x: visible;
        max-height: 210px;
    }
    &__cell {
        font-size: 12px;
        line-height: 1.1em;
        word-break: break-all;
    }
    &__dialog {
        height: 90vh;
        max-height: 500px;
        backface-visibility: hidden;
    }
    &__dialog-actions {
        justify-content: space-between;
    }
}
</style>
